import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Store, TrendingUp, Users, Truck, Loader2, CheckCircle2,
} from "lucide-react";

const CATEGORIES_AR = ["مطعم", "سوبرماركت", "تجميل", "صيدلية", "زهور", "أخرى"];
const CATEGORIES_EN = ["Restaurant", "Supermarket", "Beauty", "Pharmacy", "Flowers", "Other"];

export default function Partner() {
  const navigate = useNavigate();
  const { lang, isRTL } = useLang();
  const Chevron = isRTL ? ChevronRight : ChevronLeft;
  const cats = lang === "ar" ? CATEGORIES_AR : CATEGORIES_EN;

  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    phone: "",
    email: "",
    city: "بحرة",
    category: cats[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const benefits = [
    {
      icon: TrendingUp,
      title: lang === "ar" ? "زيادة المبيعات" : "Boost sales",
      desc: lang === "ar" ? "وصول لآلاف العملاء الجدد كل شهر" : "Reach thousands of new customers monthly",
    },
    {
      icon: Truck,
      title: lang === "ar" ? "أسطول توصيل" : "Delivery fleet",
      desc: lang === "ar" ? "سائقون محترفون يخدمون متجرك" : "Professional drivers serving your store",
    },
    {
      icon: Users,
      title: lang === "ar" ? "إدارة مرنة" : "Easy management",
      desc: lang === "ar" ? "لوحة تحكم سهلة لإدارة الطلبات والقائمة" : "Easy dashboard for orders & menu",
    },
  ];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.phone.trim()) {
      toast.error(lang === "ar" ? "الرجاء تعبئة الحقول المطلوبة" : "Please fill required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partner-applications", form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "تعذّر إرسال الطلب" : "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-md mx-auto bg-white min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div className="w-24 h-24 rounded-full bg-[#E8F7EE] flex items-center justify-center mb-6">
          <CheckCircle2 className="w-12 h-12 text-[#1DB954]" strokeWidth={2.2} />
        </div>
        <h2 className="font-display text-3xl font-black text-[#1A1A1A] mb-3">
          {lang === "ar" ? "تم استلام طلبك!" : "Application received!"}
        </h2>
        <p className="text-[#6B6B6B] text-sm leading-relaxed mb-8 max-w-xs">
          {lang === "ar"
            ? "شكراً لاهتمامك بالانضمام إلى جايك. سيتواصل معك فريقنا خلال 24-48 ساعة لمناقشة الخطوات التالية."
            : "Thanks for your interest in joining Jayek. Our team will reach out within 24-48 hours to discuss next steps."}
        </p>
        <button
          onClick={() => navigate("/")}
          className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-3 font-bold"
          data-testid="partner-success-home"
        >
          {lang === "ar" ? "العودة للرئيسية" : "Back to home"}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen pb-20">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#F2F2F2] px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="partner-back" aria-label="back">
          <Chevron className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
        </button>
        <h1 className="font-bold text-lg text-[#1A1A1A]">
          {lang === "ar" ? "كن شريكنا" : "Become a partner"}
        </h1>
      </div>

      <div className="px-5 pt-6">
        {/* Hero */}
        <div className="rounded-3xl p-6 mb-6 text-white" style={{ background: "linear-gradient(135deg, #E05D3A 0%, #C24B2C 100%)" }}>
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-3">
            <Store className="w-7 h-7 text-white" strokeWidth={2.2} />
          </div>
          <h2 className="font-display text-2xl font-black mb-2">
            {lang === "ar" ? "أضِف علامتك التجارية معنا" : "Add your brand"}
          </h2>
          <p className="text-white/85 text-sm leading-relaxed">
            {lang === "ar"
              ? "انضم لشبكة شركاء جايك في بحرة، وصِل لعملاء جدد وارفع مبيعاتك."
              : "Join Jayek partner network in Bahrah, reach new customers, grow your sales."}
          </p>
        </div>

        {/* Benefits */}
        <p className="text-xs font-bold text-[#8B8B8B] mb-3 uppercase tracking-wider">
          {lang === "ar" ? "لماذا جايك؟" : "Why Jayek?"}
        </p>
        <div className="space-y-3 mb-8">
          {benefits.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3 bg-white border border-[#F2F2F2] rounded-2xl p-4">
              <div className="w-11 h-11 rounded-2xl bg-[#FEF0EE] flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#E05D3A]" strokeWidth={2.2} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-[15px] text-[#1A1A1A] mb-0.5">{title}</p>
                <p className="text-xs text-[#6B6B6B] leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="bg-white border border-[#F2F2F2] rounded-3xl p-5 space-y-4">
          <h3 className="font-bold text-[#1A1A1A] text-lg mb-1">
            {lang === "ar" ? "بيانات التسجيل" : "Application form"}
          </h3>

          <Field
            label={lang === "ar" ? "اسم النشاط التجاري *" : "Business name *"}
            value={form.business_name}
            onChange={(v) => update("business_name", v)}
            testId="partner-business"
          />
          <Field
            label={lang === "ar" ? "اسم المسؤول *" : "Contact name *"}
            value={form.contact_name}
            onChange={(v) => update("contact_name", v)}
            testId="partner-contact"
          />
          <Field
            label={lang === "ar" ? "رقم الجوال *" : "Phone *"}
            value={form.phone}
            onChange={(v) => update("phone", v)}
            type="tel"
            dir="ltr"
            placeholder="+966 5X XXX XXXX"
            testId="partner-phone"
          />
          <Field
            label={lang === "ar" ? "البريد الإلكتروني" : "Email"}
            value={form.email}
            onChange={(v) => update("email", v)}
            type="email"
            dir="ltr"
            testId="partner-email"
          />
          <Field
            label={lang === "ar" ? "المدينة" : "City"}
            value={form.city}
            onChange={(v) => update("city", v)}
            testId="partner-city"
          />

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
              {lang === "ar" ? "فئة النشاط" : "Category"}
            </label>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => update("category", c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    form.category === c
                      ? "bg-[#E05D3A] text-white"
                      : "bg-[#FEF0EE] text-[#E05D3A] border border-[#FEF0EE]"
                  }`}
                  data-testid={`partner-cat-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
              {lang === "ar" ? "رسالتك (اختياري)" : "Message (optional)"}
            </label>
            <textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              maxLength={500}
              rows={3}
              placeholder={lang === "ar" ? "أخبرنا عن نشاطك..." : "Tell us about your business..."}
              className="w-full py-3 px-4 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] outline-none transition-colors text-sm resize-none"
              data-testid="partner-message"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-2xl bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-4 font-bold text-[15px] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="partner-submit"
          >
            {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
            {lang === "ar"
              ? (submitting ? "جاري الإرسال..." : "إرسال الطلب")
              : (submitting ? "Sending..." : "Submit application")}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", dir = "auto", placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className="w-full py-3 px-4 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] outline-none transition-colors"
        data-testid={testId}
      />
    </div>
  );
}
